import React from 'react';
import logo from '../../assets/logo-lockup.png';
import LegalFooterLinks from '../Legal/LegalFooterLinks';
import { PRICING_DISCLAIMER } from '../../utils/disclaimers';
import { SERVICE_AREA_LABEL, SUPPORT_EMAIL } from '../../utils/publicSite';
import styles from './PublicLayout.module.css';

/**
 * A real link to a public page that, on a plain click, moves between public
 * views in place (App.js has no router). Modified clicks (new tab, etc.) and
 * missing handlers fall back to normal navigation.
 *
 * @param {object} props
 * @param {string} props.to - Path, optionally with a #section, e.g. "/#faq".
 * @param {(path: string) => void} [props.onNavigate]
 */
export function PublicLink({ to, onNavigate, children, ...rest }) {
  const handleClick = (event) => {
    if (!onNavigate) return;
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    onNavigate(to);
  };
  return (
    <a href={to} onClick={handleClick} {...rest}>
      {children}
    </a>
  );
}

const NAV_LINKS = [
  { to: '/#how-it-works', label: 'How it works', view: null },
  { to: '/pricing', label: 'Pricing', view: 'pricing' },
  { to: '/#faq', label: 'FAQ', view: null },
];

/**
 * Header, service-area strip and footer shared by the signed-out pages
 * (landing, pricing, public listing).
 *
 * @param {object} props
 * @param {string} props.current - The public view on screen ('landing', 'pricing', 'home').
 * @param {(path: string) => void} props.onNavigate - Move to another public path.
 * @param {() => void} props.onSignIn
 * @param {() => void} props.onSignUp
 */
export default function PublicLayout({ current, onNavigate, onSignIn, onSignUp, children }) {
  return (
    <div className={styles.shell}>
      <a className={styles.skipLink} href="#public-main">Skip to content</a>
      <p className={styles.strip} data-testid="service-area-strip">
        Now serving homes in <strong>{SERVICE_AREA_LABEL}</strong> only.{' '}
        <PublicLink to="/#waitlist" onNavigate={onNavigate}>
          Somewhere else? Join the waitlist
        </PublicLink>
      </p>

      <header className={styles.header} data-testid="public-header">
        <div className={styles.headerInner}>
          <PublicLink to="/" onNavigate={onNavigate} className={styles.brand} aria-label="cutthe6.com home">
            <img src={logo} alt="cutthe6.com" className={styles.brandLogo} />
          </PublicLink>

          <nav className={styles.nav} aria-label="Main">
            {NAV_LINKS.map((link) => (
              <PublicLink
                key={link.to}
                to={link.to}
                onNavigate={onNavigate}
                className={styles.navLink}
                aria-current={link.view && link.view === current ? 'page' : undefined}
              >
                {link.label}
              </PublicLink>
            ))}
          </nav>

          <div className={styles.actions}>
            <button type="button" className={styles.signIn} onClick={onSignIn}>
              Sign in
            </button>
            <button type="button" className={styles.getStarted} onClick={onSignUp}>
              Get started
            </button>
          </div>
        </div>
      </header>

      <main id="public-main" className={styles.main} tabIndex={-1}>
        {children}
      </main>

      <footer className={styles.footer}>
        <div className={styles.footerInner}>
          <div className={styles.footerBrand}>
            <p className={styles.footerWordmark}>cutthe6.com</p>
            <p className={styles.footerTagline}>
              Software for selling your own home, and for buying from an owner. Live in {SERVICE_AREA_LABEL}.
            </p>
          </div>

          <nav className={styles.footerCol} aria-label="Sell">
            <p className={styles.footerHeading}>Sell</p>
            <PublicLink to="/sell" onNavigate={onNavigate}>Start your listing</PublicLink>
            <PublicLink to="/pricing" onNavigate={onNavigate}>Pricing</PublicLink>
            <PublicLink to="/#how-selling" onNavigate={onNavigate}>How selling works</PublicLink>
          </nav>

          <nav className={styles.footerCol} aria-label="Buy">
            <p className={styles.footerHeading}>Buy</p>
            <button type="button" className={styles.footerButton} onClick={onSignUp}>
              Create a free account
            </button>
            <PublicLink to="/#how-buying" onNavigate={onNavigate}>How buying works</PublicLink>
            <PublicLink to="/#faq" onNavigate={onNavigate}>FAQ</PublicLink>
          </nav>

          <div className={styles.footerCol}>
            <p className={styles.footerHeading}>Help</p>
            <a href={`mailto:${SUPPORT_EMAIL}`} data-testid="support-contact">{SUPPORT_EMAIL}</a>
            <LegalFooterLinks variant="dark" newTab={false} className={styles.footerLegal} />
          </div>
        </div>
        <div className={styles.fineprint}>
          <p>{PRICING_DISCLAIMER}</p>
          <p>© {new Date().getFullYear()} cutthe6.com</p>
        </div>
      </footer>
    </div>
  );
}
